import LoadingButton, { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { Box, CircularProgress, Theme } from '@mui/material';
import { useTheme } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { useEffect, useRef, useState } from "react";

// Visually-hidden CSS: present in the accessibility tree but invisible.
const visuallyHiddenSx = {
  position: 'absolute' as const,
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap' as const,
  border: 0,
};

let lastSubmitTrigger: HTMLButtonElement | null = null;

export const focusLastSubmitTrigger = (): boolean => {
  const el = lastSubmitTrigger;
  lastSubmitTrigger = null;
  if (el && document.contains(el) && !el.disabled) {
    el.focus();
    return true;
  }
  return false;
};

export interface MFSubmitButtonProps
  extends Omit<LoadingButtonProps, 'children' | 'loading'> {
  label?: string;
  loadingEnable?: boolean;
}

const MFSubmitButton = ({
  label = 'Submit',
  variant = 'contained',
  fullWidth = true,
  loadingEnable,
  disabled = false,
  sx,
  ...props
}: MFSubmitButtonProps): JSX.Element => {
  const theme = useTheme()
  const formikContext = useFormikContext();
  // const formik = useFormik()
  const { isSubmitting: loading } = formikContext;

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (loadingEnable && buttonRef.current) {
      lastSubmitTrigger = buttonRef.current;
    }
  }, [loadingEnable]);

  const containedVariantStyle = {
    color: 'common.white',
    backgroundColor: 'primary.700',
    padding: "10px",
    ...theme.typography.body_600
  }

  const outlinedVariantStyle = {
    color: 'text.secondary',
    backgroundColor: 'transparent',
    borderColor: 'text.secondary',
    borderWidth: '0.5px',
    borderStyle: 'solid',
    padding: "10px",
    "&:hover, &.Mui-focusVisible": { borderColor: 'text.secondary', backgroundColor: "background.default" },
    ...theme.typography.body_600
  }

  let currentStyle = {}

  if (variant === "contained") {
    currentStyle = containedVariantStyle
  } else if (variant === "outlined") {
    currentStyle = outlinedVariantStyle
  }

  return (
    <>
      <Box
        component="span"
        role="status"
        aria-live="polite"
        sx={visuallyHiddenSx}
      >
        {loadingEnable ? 'Processing request, please wait' : ''}
      </Box>
      <LoadingButton
        ref={buttonRef}
        loadingPosition="start"
        type="submit"
        loading={loadingEnable}
        aria-busy={loadingEnable ? true : undefined}
      //this is custom spinner which has more contrast then the default mui loading spinner
        loadingIndicator={
          <CircularProgress
            size={18}
            thickness={5}
            sx={(theme: Theme) => ({
              color:
                variant === 'outlined'
                  ? theme.palette.text.primary
                  : theme.palette.common.white,
            })}
          />
        }
        variant={variant}
        sx={[
          (theme: Theme) => ({ ...currentStyle, ...theme.typography.body_600 }),
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        fullWidth={fullWidth}
        disabled={disabled}
        {...props}>
        {label}
      </LoadingButton >
    </>
  );
  // return (
  //   <Button
  //     type="submit"
  //     fullWidth={fullWidth}
  //     variant={variant}
  //     sx={{ mt: 3, mb: 2, color: 'common.white', lineHeight: 1.5, fontSize: 14 }}
  //     disabled={disabled}
  //     {...props}>
  //     {l}
  //   </Button>
  // );
};
export default MFSubmitButton;
